import React, { createContext, useState, useContext, useEffect } from 'react';
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/StorageUtils';
import { normalizeVerbatimDefectStatus } from '../utils/VerbatimUtils';

const VerbatimContext = createContext();

export const useVerbatim = () => useContext(VerbatimContext);

export const VerbatimProvider = ({ children }) => {
  const [verbatims, setVerbatims] = useState([]);
  const [notes, setNotes] = useState([]);
  const [slides, setSlides] = useState([]);
  const [verbatimToEdit, setVerbatimToEdit] = useState(null);
  const [verbatimsToEdit, setVerbatimsToEdit] = useState([]); // Multiple verbatims for batch edit
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Effet pour charger les données depuis IndexedDB au montage du composant
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load from IndexedDB
        let storedVerbatims = await loadFromLocalStorage('storedVerbatims');
        const storedNotes = await loadFromLocalStorage('notes');
        const storedSlides = await loadFromLocalStorage('slides');

        // Vérifier que les verbatims sont un tableau
        if (!Array.isArray(storedVerbatims)) {
          console.warn('Les verbatims chargés ne sont pas un tableau, initialisation avec un tableau vide');
          storedVerbatims = [];
        }

        // Set the stored data
        setVerbatims((storedVerbatims || []).map(normalizeVerbatimDefectStatus));
        setNotes(storedNotes || []);
        setSlides(storedSlides || []);

        setIsLoaded(true);
        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        // En cas d'erreur, initialiser avec des tableaux vides
        setVerbatims([]);
        setNotes([]);
        setSlides([]);
        setIsLoaded(true);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Effet pour sauvegarder les données dans IndexedDB lorsqu'elles changent
  useEffect(() => {
    const saveData = async () => {
      if (isLoaded) {
        try {
          await saveToLocalStorage('storedVerbatims', verbatims);
          await saveToLocalStorage('notes', notes);
          await saveToLocalStorage('slides', slides);
        } catch (error) {
          console.error('Erreur lors de la sauvegarde des données:', error);
        }
      }
    };

    saveData();
  }, [verbatims, notes, slides, isLoaded]);

  const addVerbatim = (newVerbatim) => {
    if (!newVerbatim) {
      console.warn('Tentative d\'ajouter un verbatim nul ou non défini');
      return;
    }
    
    // S'assurer que le verbatim a un ID
    if (!newVerbatim.id && newVerbatim.id !== 0) {
      console.warn('Verbatim sans ID, attribution d\'un ID générique');
      newVerbatim = { ...newVerbatim, id: Date.now() };
    }
    
    setVerbatims((prevVerbatims) => {
      // Garantir que prevVerbatims est toujours un tableau
      const existingVerbatims = Array.isArray(prevVerbatims) ? prevVerbatims : [];
      
      if (!Array.isArray(prevVerbatims)) {
        console.warn('prevVerbatims n\'est pas un tableau, initialisation avec un tableau vide');
      }

      let adjustedVerbatim = normalizeVerbatimDefectStatus({ ...newVerbatim });
      
      // Vérifier les doublons d'ID et incrémenter si nécessaire
      while (existingVerbatims.some((v) => v.id === adjustedVerbatim.id)) {
        adjustedVerbatim.id += 1; // Increment the ID if it already exists
      }
      
      return [...existingVerbatims, adjustedVerbatim];
    });
  };
  
  

  // Function to delete a specific verbatim by ID
  const deleteVerbatim = (verbatimId) => {
    setVerbatims((prevVerbatims) => prevVerbatims.filter((v) => v.id !== verbatimId));
  };

  const deleteVerbatims = () => {
    if (window.confirm('Are you sure you want to delete all the selected verbatims?')) {
      if (verbatimsToEdit.length > 0) {
        setVerbatims((prevVerbatims) => prevVerbatims.filter((v) => !verbatimsToEdit.includes(v.id)));
        setVerbatimsToEdit([]); // Clear selection after deletion
      }
    }
  };

  // Function to edit a verbatim by ID
  const editVerbatim = (id, updatedData) => {
    setVerbatims((prevVerbatims) =>
      prevVerbatims.map((verbatim) =>
        verbatim.id === id
          ? normalizeVerbatimDefectStatus({ ...verbatim, ...updatedData })
          : verbatim
      )
    );
  };

  const updateLabelInVerbatims = (oldLabel, newLabel, oldLabelType, newLabelType, oldTypeSentimentEnabled, newTypeSentimentEnabled) => {
    // Filter the verbatims that contain the old label in the specified old label type
    const affectedVerbatims = verbatims.filter((verbatim) => {
      const labelsOfType = verbatim.labels[oldLabelType];

      // Check if labelsOfType is defined and an array, then check for matching labels
      return Array.isArray(labelsOfType) && labelsOfType.some((l) => l && l.name && l.name.trim().toLowerCase() === oldLabel.trim().toLowerCase());
    });

    // Warn the user if verbatims will be affected and ask for confirmation
    if (affectedVerbatims.length > 0) {
      let confirmationMessage = `${affectedVerbatims.length} verbatims will be updated with the new label name "${newLabel}" under type "${newLabelType}".`;

      // Add sentiment state change warnings
      if (oldTypeSentimentEnabled && !newTypeSentimentEnabled) {
        confirmationMessage += `\n\nYou are changing from a sentiment-enabled type to a non-sentiment-enabled type. The sentiment states of the labels (e.g., "positive", "negative", or "mixed") will be changed to "active".`;
      } else if (!oldTypeSentimentEnabled && newTypeSentimentEnabled) {
        confirmationMessage += `\n\nYou are changing from a non-sentiment-enabled type to a sentiment-enabled type. The labels will now be set to the sentiment state "positive".`;
      }

      confirmationMessage += `\n\nDo you want to proceed with these changes?`;
      const confirmed = window.confirm(confirmationMessage);

      if (!confirmed) return; // If the user cancels, stop the function here
    }

    // Now, we update the verbatims in which the old label is found
    setVerbatims((prevVerbatims) =>
      prevVerbatims.map((verbatim) => {
        // Clone the labels object to avoid directly mutating state
        let updatedLabels = { ...verbatim.labels };

        // Get the array of labels for the old label type
        const labelArray = updatedLabels[oldLabelType];

        // If the old label type contains the oldLabel, update it
        if (Array.isArray(labelArray) && labelArray.some((l) => l && l.name && l.name.trim().toLowerCase() === oldLabel.trim().toLowerCase())) {
          // Remove the old label from the old label type array, ensure label has a name
          updatedLabels[oldLabelType] = labelArray.filter(
            (label) => label && label.name && label.name.trim().toLowerCase() !== oldLabel.trim().toLowerCase()
          );

          // If the new label type is different, move the label to the new label type
          if (newLabelType !== oldLabelType) {
            // Make sure the new label type exists in the labels object
            if (!updatedLabels[newLabelType]) {
              updatedLabels[newLabelType] = [];
            }

            // Find the label to move, ensure it exists and has a name
            const labelToMove = labelArray.find((l) => l && l.name && l.name.trim().toLowerCase() === oldLabel.trim().toLowerCase());
            if (labelToMove) {
              // Handle sentiment state changes when moving to the new label type
              let updatedLabel = { ...labelToMove, name: newLabel, type: newLabelType };

              if (!oldTypeSentimentEnabled && newTypeSentimentEnabled) {
                // Changing to sentiment-enabled: set sentiment state to 'positive'
                updatedLabel.state = 'positive';
              } else if (oldTypeSentimentEnabled && !newTypeSentimentEnabled) {
                // Changing to non-sentiment-enabled: set sentiment state to 'active'
                updatedLabel.state = 'active';
              }

              // Add the updated label to the new label type array
              updatedLabels[newLabelType].push(updatedLabel);
            }

            // If the old label type is now empty, remove it
            if (updatedLabels[oldLabelType].length === 0) {
              delete updatedLabels[oldLabelType];
            }
          } else {
            // If the label type has not changed, just update the label name in the same array
            let updatedLabel = { name: newLabel, type: oldLabelType };

            // Manage sentiment state if the label type remains the same
            const existingLabel = labelArray.find((l) => l && l.name.trim().toLowerCase() === oldLabel.trim().toLowerCase());
            if (existingLabel) {
              updatedLabel.state = existingLabel.state;
            }

            updatedLabels[oldLabelType].push(updatedLabel);
          }
        }

        // Return the updated verbatim object with the modified labels
        return { ...verbatim, labels: updatedLabels };
      })
    );
  };

  // Function to update all labels of a specific type across all verbatims
  // Now it accounts for both sentiment changes and renaming the label type
  const updateAllLabelsInVerbatims = (oldLabelType, newLabelType, oldTypeSentimentEnabled, newTypeSentimentEnabled) => {
    // Filter the verbatims that contain labels of the given old type
    const affectedVerbatims = verbatims.filter((verbatim) => {
      // Check if the verbatim contains labels of the given old type
      return Array.isArray(verbatim.labels[oldLabelType]) && verbatim.labels[oldLabelType].length > 0;
    });

    // Build a more detailed confirmation message based on the sentiment changes
    let confirmationMessage = `${affectedVerbatims.length} verbatims will be updated with the new label type "${newLabelType}".`;

    if (oldTypeSentimentEnabled && !newTypeSentimentEnabled) {
      confirmationMessage += `\n\nYou are changing from a sentiment-enabled type to a non-sentiment-enabled type. This means the sentiment states of the labels (e.g., "positive", "negative", or "mixed") will be changed to "active".`;
    } else if (!oldTypeSentimentEnabled && newTypeSentimentEnabled) {
      confirmationMessage += `\n\nYou are changing from a non-sentiment-enabled type to a sentiment-enabled type. This means the labels will now be set to the sentiment state "positive".`;
    }

    confirmationMessage += `\n\nDo you want to proceed with these changes?`;

    // Warn the user if verbatims will be affected and ask for confirmation
    if (affectedVerbatims.length > 0) {
      const confirmed = window.confirm(confirmationMessage);
      if (!confirmed) return; // If the user cancels, stop the function here
    }

    // Now, we update the verbatims in which the labels of the old type are found
    setVerbatims((prevVerbatims) =>
      // Map through each verbatim (to update labels for each one that needs it)
      prevVerbatims.map((verbatim) => {
        // Clone the labels object to avoid directly mutating state
        let updatedLabels = { ...verbatim.labels };

        // Check if the verbatim has the oldLabelType
        if (Array.isArray(updatedLabels[oldLabelType])) {
          // Update the labels under oldLabelType, managing sentiment state changes
          updatedLabels[oldLabelType] = updatedLabels[oldLabelType].map((label) => {
            let updatedLabelData = { ...label };

            // Manage sentiment state change
            if (!oldTypeSentimentEnabled && newTypeSentimentEnabled) {
              // If switching to sentiment-enabled, set the state to 'positive'
              updatedLabelData.state = 'positive';
            } else if (oldTypeSentimentEnabled && !newTypeSentimentEnabled) {
              // If switching to non-sentiment, set the state to 'active'
              updatedLabelData.state = 'active';
            }

            // Update the label type in case of renaming
            updatedLabelData.type = newLabelType;

            return updatedLabelData;
          });

          // If the label type name has changed, move the labels to the new type array
          if (newLabelType !== oldLabelType) {
            // Ensure the new type exists in the updated labels object
            if (!updatedLabels[newLabelType]) {
              updatedLabels[newLabelType] = [];
            }

            // Move the updated labels to the new type
            updatedLabels[newLabelType] = [...updatedLabels[newLabelType], ...updatedLabels[oldLabelType]];
            delete updatedLabels[oldLabelType];  // Remove the old type from labels
          }
        }

        // Return the updated verbatim object with the modified labels
        return { ...verbatim, labels: updatedLabels };
      })
    );
  };

  const deleteLabelInVerbatims = (labelToDelete, labelType) => {
 
    // Find all verbatims that will be affected by the label deletion
    const affectedVerbatims = verbatims.filter((verbatim) => {
      const labelArray = verbatim.labels[labelType]; // Get the array of labels for the specific type
      console.log(labelArray);
      return (
        Array.isArray(labelArray) && 
        labelArray.some((l) => l && l.name === labelToDelete) // Ensure `l` is not null or undefined
      );
    });
    

    // If no verbatims are affected, simply return 0
    if (affectedVerbatims.length === 0) {
      return 0;  // No verbatims to update
    }

    // Ask for confirmation with the number of affected verbatims
    const confirmed = window.confirm(
      `${affectedVerbatims.length} verbatims will be affected by the deletion of the label "${labelToDelete}". Do you want to proceed?`
    );

    // If the user confirms, perform the deletion
    if (confirmed) {
      setVerbatims((prevVerbatims) =>
        prevVerbatims.map((verbatim) => {
          let updatedLabels = { ...verbatim.labels };
    
          // Ensure the label type exists in this verbatim
          if (updatedLabels[labelType]) {
            // Filter out the label to delete from the label type's array
            updatedLabels[labelType] = updatedLabels[labelType].filter(
              (l) => l && l.name !== labelToDelete // Ensure `l` is valid before accessing `l.name`
            );
    
            // If the label type's array is now empty, remove the entire label type
            if (updatedLabels[labelType].length === 0) {
              delete updatedLabels[labelType]; // Remove the key if no labels remain
            }
          }
    
          // Return the updated verbatim with modified labels
          return { ...verbatim, labels: updatedLabels };
        })
      );

      // Return the count of affected verbatims
      return affectedVerbatims.length;
    } else {
      // If the user cancels, return 0 affected verbatims
      return 0;
    }
  };

  const toggleUsedStatus = (id) => {
    setVerbatims((prevVerbatims) =>
      prevVerbatims.map((verbatim) =>
        verbatim.id === id
          ? { ...verbatim, used: !verbatim.used } // Toggle the "used" status
          : verbatim
      )
    );
  };
  

  // Si vous êtes en train de charger les données, affichez éventuellement un indicateur de chargement
  if (isLoading) {
    return <div>Loading verbatims data...</div>;
  }

  return (
    <VerbatimContext.Provider value={{ 
      verbatims, setVerbatims, 
      notes, setNotes, 
      slides, setSlides, 
      verbatimsToEdit, setVerbatimsToEdit, 
      verbatimToEdit, setVerbatimToEdit, 
      getVerbatims: () => verbatims, 
      addVerbatim, 
      deleteVerbatim, 
      deleteVerbatims,
      editVerbatim, 
      updateLabelInVerbatims, 
      updateAllLabelsInVerbatims, 
      deleteLabelInVerbatims, 
      toggleUsedStatus
    }}>
      {children}
    </VerbatimContext.Provider>
  );
};
